import { useState, useMemo, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";

import FormContentManager from "../../../components/common/form/FormContentManager";
import TableCell from "../../../components/common/ui/TableCell";
import PeopleCell from "../../../components/common/people/PeopleCell";

import { getQualityMeta } from "../../../api/quality/meta";

export default function Inspection_new() {
  const { id: projectId, dns: paramDns } = useParams();
  const [dns, setDns] = useState(paramDns || "");

  /* ===============================
     HEADER STATE
  =============================== */
  const [title] = useState("검측요청서");
  const [requestTitle, setRequestTitle] = useState("");
  const [requestDate, setRequestDate] = useState("");
  const [locationWork, setLocationWork] = useState("");
  const [inspectionPart, setInspectionPart] = useState("");
  const [inspectionItem, setInspectionItem] = useState("");

  /* ===============================
     PEOPLE
  =============================== */
  const [manager, setManager] = useState(null); // 작성자
  const [siteAgent, setSiteAgent] = useState([]); // 현장대리인
  const [receiver, setReceiver] = useState([]); // 참조

  /* ===============================
     로그인 사용자 → 작성자 자동
  =============================== */
  useEffect(() => {
    if (paramDns) return;

    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      if (u?.id) {
        setManager({
          id: u.id,
          name: u.name,
        });
      }
    } catch { }
  }, [paramDns]);

  /* ===============================
     기존 문서 로드
  =============================== */
  useEffect(() => {
    if (!paramDns) return;

    (async () => {
      try {
        const res = await getQualityMeta({ dns: paramDns });

        // 🔍 디버깅: 전체 응답 확인
        console.log("📄 [Inspection_new] 전체 응답:", res);
        console.log("📄 [Inspection_new] res.doc:", res?.doc);
        console.log("📄 [Inspection_new] res.meta:", res?.meta);
        console.log("📄 [Inspection_new] res.status:", res?.status);

        // ✅ 수정: res.meta에 바로 form과 officialMeta가 있음
        const actualMeta = res?.meta || {};
        const o = actualMeta?.officialMeta || {};
        const f = actualMeta?.form || {};

        console.log("📋 [Inspection_new] actualMeta:", actualMeta);
        console.log("📋 [Inspection_new] officialMeta:", o);
        console.log("📋 [Inspection_new] form:", f);

        // ⭐ 반려된 문서인 경우 dns를 초기화 (새 문서로 생성)
        const isRejected = res?.doc?.status === "rejected" || res?.status === "rejected";

        console.log("🔍 [Inspection_new] isRejected:", isRejected);

        if (isRejected) {
          console.log("🔄 반려 문서 재작성 모드: 새 문서로 생성됩니다");
          setDns("");  // 반려된 문서는 dns를 비워서 새 문서로 생성
        } else {
          setDns(paramDns);  // 일반 문서는 기존 dns 유지 (수정 모드)
        }

        // 데이터 설정
        setRequestTitle(f.title || "");
        setRequestDate(o.request_date || "");
        setLocationWork(o.location_work || "");
        setInspectionPart(o.inspection_part || "");
        setInspectionItem(o.inspection_item || "");

        if (o.manager) setManager(o.manager);
        if (o.site_agent) setSiteAgent([o.site_agent]);
        if (o.receiver) setReceiver([o.receiver]);

        console.log("✅ [Inspection_new] 데이터 로드 완료");
      } catch (err) {
        console.error("❌ [Inspection_new] 데이터 로드 실패:", err);
      }
    })();
  }, [paramDns]);

  /* ===============================
     SUMMARY
  =============================== */
  const summaryConfig = useMemo(
    () => ({
      title: "검측요청서 요약",
      sections: [
        { label: "검측 요청일", value: requestDate || "-" },
        { label: "위치 및 공종", value: locationWork || "-" },
        { label: "검측 부위", value: inspectionPart || "-" },
        { label: "작성자", value: manager?.name || "-" },
        { label: "현장대리인", value: siteAgent[0]?.name || "-" },
        { label: "수신자", value: receiver[0]?.name || "-" },
      ],
    }),
    [requestDate, locationWork, inspectionPart, manager, siteAgent, receiver]
  );

  /* ===============================
     SAVE BODY (❌ APR 제거)
  =============================== */
  const getBody = useCallback(() => {
    return {
      values: {
        project: projectId,
        module: "quality",
        doc_type: "MI",
        // dns는 제거! FormContentManager의 params.dns가 사용됨

        meta: {
          form: {
            title: requestTitle || "[검측요청서]",
          },
          officialMeta: {
            request_date: requestDate || null,
            location_work: locationWork,
            inspection_part: inspectionPart,
            inspection_item: inspectionItem,
            manager,
            site_agent: siteAgent[0] || null,
            receiver: receiver[0] || null,
          },
          __client__: {
            form_type: "MI",
          },
        },
      },
    };
  }, [
    projectId,
    // dns 제거! (dependency에서도)
    requestTitle,
    requestDate,
    locationWork,
    inspectionPart,
    inspectionItem,
    manager,
    siteAgent,
    receiver,
  ]);

  return (
    <FormContentManager
      params={{ module: "quality", project: projectId, dns }}
      getBody={getBody}
      summaryConfig={summaryConfig}
      docName="검측요청서"
      docDescribe="공정 또는 부위에 대해 검측을 요청하는 문서입니다."
      redirectTo={`/project/${projectId}/quality/qualityboard`}
    >
      {/* 반려 문서 재작성 경고 메시지 */}
      {paramDns && !dns && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800 flex items-center gap-2">
            <span className="text-lg">⚠️</span>
            <span>
              <strong>반려된 문서를 수정 중입니다.</strong> 저장하면 새로운 문서로 생성됩니다.
            </span>
          </p>
        </div>
      )}

      {/* TITLE */}
      <div className="text-center mb-6">
        <div className="text-xl font-bold">{title}</div>
      </div>

      {/* HEADER */}
      <table className="doc-table w-full text-sm mb-4">
        <tbody>
          <tr>
            <TableCell
              label="검측 요청일"
              editing
              input={
                <input
                  type="date"
                  className="input-base"
                  value={requestDate}
                  onChange={(e) => setRequestDate(e.target.value)}
                />
              }
            />
            <TableCell label="작성자" editing input={manager?.name || "-"} />
            <TableCell
              label="현장대리인"
              editing
              input={
                <PeopleCell value={siteAgent} onChange={setSiteAgent} single />
              }
            />
          </tr>

          <tr>
            <TableCell
              label="수신자"
              colSpan={5}
              editing
              input={
                <PeopleCell value={receiver} onChange={setReceiver} single />
              }
            />
          </tr>

          <tr>
            <TableCell
              label="제목"
              colSpan={5}
              editing
              input={
                <input
                  className="input-base"
                  value={requestTitle}
                  onChange={(e) => setRequestTitle(e.target.value)}
                />
              }
            />
          </tr>
        </tbody>
      </table>

      {/* REQUEST INFO */}
      <table className="doc-table w-full text-sm mb-4">
        <tbody>
          <tr>
            <TableCell
              label="위치 및 공종"
              colSpan={5}
              editing
              input={
                <input
                  className="input-base"
                  value={locationWork}
                  onChange={(e) => setLocationWork(e.target.value)}
                />
              }
            />
          </tr>

          <tr>
            <TableCell
              label="검측 부위"
              colSpan={5}
              editing
              input={
                <input
                  className="input-base"
                  value={inspectionPart}
                  onChange={(e) => setInspectionPart(e.target.value)}
                />
              }
            />
          </tr>

          <tr>
            <TableCell
              label="검측 사항"
              colSpan={5}
              editing
              input={
                <textarea
                  className="input-base w-full h-24"
                  value={inspectionItem}
                  onChange={(e) => setInspectionItem(e.target.value)}
                />
              }
            />
          </tr>
        </tbody>
      </table>
    </FormContentManager>
  );
}
